<script>
  import { tweened } from 'svelte/motion'
  import { sineInOut } from 'svelte/easing'
  import {
    Group,
    IcosahedronBufferGeometry,
    SphereBufferGeometry,
    MeshLambertMaterial,
    Mesh,
    Vector3,
    MeshBasicMaterial,
    AdditiveBlending
  } from 'three'
  import { renderable } from './core-components/three-viz.js'
  import { scaleLinear } from 'd3-scale'
  import {
    selectedClusterType,
    stories,
    filteredStories,
    coordsRange,
    hoveredObjects,
    selectedStory,
    colorCriterium,
    colorScheme
  } from './stores.js'

  export let sceneSize = 3
  export let pointSize = 0.04
  export let defaultColor = 0x6600ff
  export let selectionColor = 0xffffff

  //bounding mesh around point, for catching mouse hover
  const selectionSensitivity = 1.8 //the larger, the less accurate you have to position your mouse when selecting
  const boundingGeometry = new SphereBufferGeometry(selectionSensitivity * pointSize)
  const boundingMaterial = new MeshBasicMaterial()
  boundingMaterial.visible = false

  let scene

  $: points = [] //list of stories: both metadata and mesh

  const tweenedCoords = tweened(null, {
    delay: 0,
    duration: 500,
    easing: sineInOut
  })

  $: scaleX = $coordsRange
    ? scaleLinear()
        .range([-sceneSize, sceneSize])
        .domain($coordsRange['spread'].x)
    : () => 0

  $: scaleY = $coordsRange
    ? scaleLinear()
        .range([-0.3, 0.3])
        .domain($coordsRange['spread'].y)
    : () => 0

  $: scaleZ = $coordsRange
    ? scaleLinear()
        .range([-sceneSize, sceneSize])
        .domain($coordsRange['spread'].z)
    : () => 0

  //update selected story by checking hovered objects
  $: {
    $selectedStory = null
    //Cycle through hovered objects. If one of them corresponds with a
    //story, select that story and break out of the loop
    for (let i=0; i<$hoveredObjects.length; i++) {
      const point = points.find(p => p.boundingSphere.uuid == $hoveredObjects[i].object.uuid)
      if (point) {
        $selectedStory = point.story
        break
      }
    }
  }

  //update points when data is updated
  $: {
    let coords = []
    $stories.forEach((story,i) => {
      let point = points.find(p => p.story == story)
      if (!point) {
        point = {
          story: story,
          coordIndex: i
        }

        const geometry = new IcosahedronBufferGeometry(pointSize)
        const material = new MeshLambertMaterial({blending: AdditiveBlending})
        const sphere = new Mesh(geometry, material)

        point.sphere = sphere //store mesh together with story metadata

        const boundingSphere = new Mesh(boundingGeometry, boundingMaterial)
        point.boundingSphere = boundingSphere


        //group.add(sphere)
        scene.add(sphere)
        scene.add(boundingSphere)
        points.push(point)
      }
      coords[i] = {
        x : scaleX(story[$selectedClusterType.prop][0]),
        y : scaleY(story[$selectedClusterType.prop][1]),
        z : scaleZ(story[$selectedClusterType.prop][2])
      }
    })
    if (coords.length > 0) tweenedCoords.set(coords)
    points = points //force the positions of points to be updated
    console.log(points)
  }

  $: {
    if ($tweenedCoords) {
      points.forEach(p => {
        const c = $tweenedCoords[p.coordIndex]
        p.sphere.position.set(c.x, c.y, c.z)
        p.boundingSphere.position.set(c.x, c.y, c.z)
      })
    }
  }

  $: {
    points.forEach(p => {
      p.sphere.visible = $filteredStories.indexOf(p.story) != -1
    })
  }

  renderable({
    setup: props => {
      scene = props.scene
    },
    render: props => {
      const { camera } = props
      points.forEach(p => {
        const col = p.story == $selectedStory
           ? selectionColor
           : $colorCriterium
                ? $colorScheme[p.story[$colorCriterium]]
                : defaultColor
        p.sphere.material.color.set(col)
      })
    }
  })
</script>
